/**
 * The prompt compiler.
 *
 * This runs in the browser, and the string it produces is exactly the string the
 * API sends — which is what lets the studio show "what gets sent" honestly
 * rather than approximating it.
 *
 * A role is a clause, not a setting. Attaching an image and choosing "outfit"
 * adds one short phrase naming that image by its position. Nothing is emitted
 * for 'none', which is how you attach a picture without saying anything about
 * it. Keeping this to one clause per input is deliberate: a long prompt full of
 * per-attribute instructions competes with the images instead of guiding them.
 */

export type Role =
  | 'none' | 'character' | 'outfit' | 'background'
  | 'pose' | 'light' | 'style' | 'object' | 'edit';

export const ROLES: { id: Role; label: string; clause: ((n: number) => string) | null }[] = [
  { id: 'none',       label: 'nothing',       clause: null },
  // The CLAUSE still says "person", and deliberately: it is prompt text aimed at
  // an image model, where "person" is the word that carries meaning. Only the
  // id and the label follow the product's vocabulary.
  { id: 'character',  label: 'the character', clause: (n) => `the person in image ${n}` },
  { id: 'outfit',     label: 'outfit',        clause: (n) => `wearing the outfit from image ${n}` },
  { id: 'background', label: 'setting',       clause: (n) => `in the setting from image ${n}` },
  { id: 'pose',       label: 'pose',          clause: (n) => `in the pose from image ${n}` },
  { id: 'light',      label: 'lighting',      clause: (n) => `lit like image ${n}` },
  { id: 'style',      label: 'look',          clause: (n) => `in the look of image ${n}` },
  { id: 'object',     label: 'the object',    clause: (n) => `with the object from image ${n}` },
  { id: 'edit',       label: 'edit this one', clause: (n) => `starting from image ${n}` },
];

const BY_ID = new Map(ROLES.map((r) => [r.id, r]));

export function roleLabel(id: Role): string {
  return BY_ID.get(id)?.label ?? id;
}

/** The default role for something pulled out of a given catalogue category. */
export function roleForCategory(category: string): Role {
  switch (category) {
    case 'outfits': return 'outfit';
    case 'places': return 'background';
    case 'objects': return 'object';
    case 'looks': return 'style';
    default: return 'none';
  }
}

export interface ComposeInput { role: Role }

/**
 * Build the prompt: what you typed, then one clause per input that has a role,
 * then the character's description if one is in play.
 */
export function compile(
  base: string,
  inputs: ComposeInput[],
  characterDescription?: string,
): string {
  const clauses: string[] = [];
  inputs.forEach((input, i) => {
    const role = BY_ID.get(input.role);
    if (role?.clause) clauses.push(role.clause(i + 1));
  });

  const parts: string[] = [];
  const trimmed = base.trim();
  if (trimmed) parts.push(sentence(trimmed));
  if (clauses.length) parts.push(sentence(clauses.join(', ')));
  const desc = characterDescription?.trim();
  if (desc) parts.push(sentence(desc));
  return parts.join(' ');
}

/** Capitalise, and end with a single full stop. */
function sentence(text: string): string {
  const t = text.replace(/\s+$/, '').replace(/\.+$/, '');
  return `${t.charAt(0).toUpperCase()}${t.slice(1)}.`;
}

/* ------------------------------------------------------------ dimensions --- */

/**
 * Every aspect ratio the service accepts, probed against the live API on
 * 2026-07-27 — the published docs still describe an older
 * square/portrait/landscape/tall/wide vocabulary that the API now rejects.
 * Ordered tall → square → wide, which is how people scan for one.
 */
export const ASPECTS: { id: string; label: string }[] = [
  { id: 'auto', label: 'Auto · the service decides' },
  { id: '9:21', label: '9:21 · ultra tall' },
  { id: '1:3',  label: '1:3 · very tall' },
  { id: '1:2',  label: '1:2 · tall' },
  { id: '9:16', label: '9:16 · tall' },
  { id: '2:3',  label: '2:3 · portrait' },
  { id: '3:4',  label: '3:4 · portrait' },
  { id: '4:5',  label: '4:5 · portrait' },
  { id: '1:1',  label: '1:1 · square' },
  { id: '5:4',  label: '5:4 · landscape' },
  { id: '4:3',  label: '4:3 · landscape' },
  { id: '3:2',  label: '3:2 · landscape' },
  { id: '16:9', label: '16:9 · wide' },
  { id: '2:1',  label: '2:1 · wide' },
  { id: '3:1',  label: '3:1 · very wide' },
  { id: '21:9', label: '21:9 · ultra wide' },
];

export const ASPECT_IDS = ASPECTS.map((a) => a.id);

/** True when no ratio is being sent and the service reads it off image 1. */
export function isAuto(aspect: string): boolean {
  return aspect === 'auto';
}

/**
 * Roughly what comes back: the long edge at 1024 (1K) or 2048 (2K), rounded to
 * a multiple of 16. Derived from the ratio rather than tabulated, so it stays
 * correct if the service adds one. The service picks its own nearest size —
 * this is for the readout, not a promise.
 *
 * On 'auto' there is nothing honest to compute — the shape is whatever the
 * service reads off image 1 — so callers must not show these numbers. Use
 * `isAuto` to branch; the square fallback is only here to keep the type total.
 */
export function dimensions(aspect: string, resolution: string): [number, number] {
  const [aw, ah] = aspect.split(':').map(Number);
  if (!aw || !ah) return resolution === '2k' ? [2048, 2048] : [1024, 1024];
  const long = resolution === '2k' ? 2048 : 1024;
  const scale = long / Math.max(aw, ah);
  const to16 = (n: number) => Math.max(16, Math.round((n * scale) / 16) * 16);
  return [to16(aw), to16(ah)];
}

/** Price of one output image, matching the API's own calculation. */
export function priceUsd(resolution: string, inputCount: number): number {
  const base = resolution === '2k' ? 0.09 : 0.045;
  return Number((base + Math.max(0, inputCount - 1) * 0.003).toFixed(4));
}

export function money(n: number): string {
  return `$${n.toFixed(n < 1 ? 3 : 2)}`;
}

/**
 * File name for a downloaded image, without the extension — the first few words
 * of the prompt (or the tile's label) so a folder of renders is scannable, plus
 * a slice of the id so two renders of the same prompt don't collide.
 */
export function fileBase(label: string, id = ''): string {
  const words = slug(label).split('-').filter(Boolean).slice(0, 6).join('-');
  const tag = slug(id).replace(/-/g, '').slice(0, 8);
  return ['fluiddream', words || 'image', tag].filter(Boolean).join('-');
}

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
